import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import PageHeader from "../../Components/Common/PageHeader";
import { Button } from "../../shadcn/components/ui/button";

import ClubDomainRequestsTable from "./ClubDomainRequestsTable/ClubDomainRequestsTable";
import { useTranslation } from "../../context/Internationalization";
import useInfinityScroll from "../../Hooks/useInfinityScroll";
import { GET_CLUB_DOMAIN_REQUESTS_SA } from "./SuperAdminService";
import useGraphql from "../../Hooks/useGraphql";
import useLog from "../../Hooks/useLog";
import ListTableSection from "../../Components/Table/ListTableSection";
import { toast } from "sonner";

const initialState = {
  requests: [],
  loading: false,
  hasMore: true,
  skip: 0,
};

const SuperAdminClubDomainRequests = () => {
  const { t } = useTranslation();
  const [state, setState] = useState(initialState);
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();

  const getRequests = useCallback((skip = 0) => {
    setState((previousState) => ({ ...previousState, loading: true }));

    axiosGraphQL({
      query: GET_CLUB_DOMAIN_REQUESTS_SA,
      variables: {
        skip: skip * 20,
        limit: 20,
      },
    })
      .then((response) => {
        const { getClubDomainRequestsSA } = response.data.data;

        setState((previousState) => ({
          ...previousState,
          skip,
          hasMore: !(getClubDomainRequestsSA?.length < 20),
          loading: false,
          requests:
            skip === 0
              ? getClubDomainRequestsSA
              : [...previousState.requests, ...getClubDomainRequestsSA],
        }));
      })
      .catch((error) => {
        errorLog(
          error,
          "SuperAdminClubDomainRequests",
          "getRequests",
          "GET_CLUB_DOMAIN_REQUESTS_SA"
        );
        setState((previousState) => ({ ...previousState, loading: false }));
        toast.error(error.message);
      });
  }, []);

  useEffect(() => {
    getRequests();
  }, [getRequests]);

  const updatePage = useCallback(async () => {
    getRequests(state.skip + 1);
  }, [state.skip, getRequests]);

  const lastElementRef = useInfinityScroll({
    updatePage,
    isLoading: state.loading,
    hasMore: state.hasMore,
  });

  const handleRefresh = () => getRequests(0);

  return (
    <div className="p-6">
      <PageHeader
        title={t("domain_requests_admin_panel")}
        actions={
          <Button type="button" variant="ghost" size="sm" onClick={handleRefresh}>
            <RefreshCw aria-hidden="true" />
            {t("refresh")}
          </Button>
        }
      />
      <ListTableSection>
        <ClubDomainRequestsTable
          lastElementRef={lastElementRef}
          requests={state.requests}
          loading={state.loading}
          emptyMessage={t("no_domain_requests_found")}
        />
      </ListTableSection>
    </div>
  );
};

export default SuperAdminClubDomainRequests;
